import { cellAtWorld, heroWalkable } from '../grid.ts';
import { iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { FallenRules, FallenState } from '../aster-types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import { besideCairn } from './cairns.ts';
import { sendaCue } from './senda.ts';
import type { NightmareType } from '@somnhold/content';

/**
 * The test: the fallen cairn (docs/sendas/explorador.md, chapter 1, act 3, «La piedra caída»; §4, «Los nudos», grande;
 * the ground in battle/aster-layout.ts). A slope of loose plates where the titan moved. One cairn has the long cord:
 * rings from it, which other cairn (the sun's or the sea's: the westmost or eastmost standing), rings from that one.
 * Each cairn read (the `mark` command, its index) shows its rings on the ground (the last two stay); the two rings
 * cross on one of the four light bases on the slope, where the fallen one stood. Standing `stand` on a base settles it
 * (the mark `cairns + its index` sends the hero there): the right one wins; a wrong one cracks, wakes a band and sends
 * the Roller rolling at once. The obvious base, the nearest to the long cord's cairn, is a ring short.
 *
 * The Roller (the act's boss, `roller`: an ogre in the sim, its own rule here): it comes down the slope at `at` (or at
 * the first wrong base), walks at the hero and bites; every `every` it curls (`warn`, the plates of its back closing)
 * and rolls straight where the hero was, striking and pushing whoever it runs over; a standing cairn in its way stops
 * it, stunned `stun` (the cairn doesn't fall). Slingers off the top and crows the while: a roll isn't dodged by
 * distance (it crosses the whole slope), only by stepping out of its line, or by a cairn between.
 *
 * Cost: the Roller's step and a handful of cairns and bases a tick; what else comes hunts by the common rule.
 */

const fresh = (r: FallenRules): FallenState => ({
  read: r.cairns.map(() => 0),
  rings: [-1, -1],
  bases: r.bases.map(() => 0),
  job: -1,
  held: 0,
  hp: 0,
  at: -1,
  on: -1,
  since: 0,
  wrongs: 0,
  next: { sling: r.sling.first, crow: r.crows.first },
  roller: { id: 0, mode: 0, t: 0, dx: 0, dy: 0, left: 0, struck: false, next: 0, bite: 0, rolls: 0, stuns: 0 },
  done: -1,
});

/** The hero's taps: a cairn (its index) to read; a base (cairns + its index) to stand on. */
export const fallenOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.fallen;
  const w = s.senda?.fallen;
  if (!r || !w || at < 0 || w.done >= 0) return;
  if (at < r.cairns.length) {
    const c = r.cairns[at] as FallenRules['cairns'][number];
    w.job = at;
    w.held = 0;
    const p = besideCairn(s, c.x, c.y, h.x, h.y);
    orderMove(s, h, p.x, p.y);
  } else {
    const b = r.bases[at - r.cairns.length];
    if (!b) return;
    w.job = -1;
    orderMove(s, h, b.x, b.y);
  }
  h.target = 0;
  h.forced = false;
};

const band = (s: BattleState, r: FallenRules, kind: NightmareType, n: number, near?: { x: number; y: number }): void => {
  for (let k = 0; k < n; k++) {
    let p = r.rim[rngInt(s.rng, r.rim.length)] as { x: number; y: number };
    if (near && kind === 'crow') p = { x: near.x + (k % 2 ? 6000 : -6000), y: near.y - 3000 + k * 600 };
    const e = spawnEnemy(s, kind, 1, false, 0, p.x, p.y);
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
    e.anim = 'walk';
  }
};

/** The Roller comes down from the top of the slope. */
const rollerIn = (s: BattleState, r: FallenRules, w: FallenState, cue: (c: string) => void): void => {
  if (w.roller.id) return;
  const e = spawnEnemy(s, 'ogre', 1, false, 0, r.roller.from.x, r.roller.from.y, r.roller.hp);
  e.anim = 'walk';
  w.roller.id = e.id;
  w.roller.mode = 0;
  w.roller.next = s.tick + r.roller.every;
  s.bossId = e.id;
  ev(s, { t: 'senda', kind: 'rollerIn', npc: '', x: e.x, y: e.y });
  cue('roller');
};

/** The cairn standing at a cell (its index; −1 none). */
const cairnAt = (r: FallenRules, cell: number): number => {
  for (let i = 0; i < r.cairns.length; i++) if ((r.cairns[i] as FallenRules['cairns'][number]).cell === cell) return i;
  return -1;
};

/** Whether the Roller's id is this one (its own rule: the enemies' tick leaves it here). */
export const fallenOwn = (s: BattleState, id: number): boolean => !!s.senda?.fallen && s.senda.fallen.roller.id === id;

/**
 * The Roller's rule (`fallenHold`): walking at the hero and biting; curling, its way set where the hero is; rolling
 * straight; resting. Stunned, the common rule counts it down.
 */
export const fallenHold = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.fallen;
  const w = s.senda?.fallen;
  if (!r || !w || e.id !== w.roller.id || e.stun > 0) return false;
  const R = r.roller;
  const p = w.roller;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (p.mode === 0) {
    if (h && s.tick >= p.next) {
      const dx = h.x - e.x;
      const dy = h.y - e.y;
      const d = imax(1, isqrt(dx * dx + dy * dy));
      p.dx = idiv(dx * 1000, d);
      p.dy = idiv(dy * 1000, d);
      p.mode = 1;
      p.t = s.tick + R.warn;
      e.facing = iatan2(dy, dx);
      e.anim = 'cast';
      e.animT = 0;
      ev(s, { t: 'senda', kind: 'rollWarn', npc: '', x: e.x, y: e.y, n: e.facing });
      return true;
    }
    if (!h) return true;
    e.facing = iatan2(h.y - e.y, h.x - e.x);
    if (withinRange(e.x, e.y, h.x, h.y, R.bite.reach)) {
      e.anim = 'idle';
      if (s.tick >= p.bite) {
        p.bite = s.tick + R.bite.every;
        e.anim = 'attack';
        e.animT = 0;
        damageHero(s, h, mulBp(h.stats.maxHp, R.bite.heroBp), e, 'physical', false);
      }
      return true;
    }
    e.anim = 'walk';
    stepToward(e, h.x, h.y, R.pace);
    return true;
  }
  if (p.mode === 1) {
    e.anim = 'cast';
    if (s.tick < p.t) return true;
    p.mode = 2;
    p.left = R.len;
    p.struck = false;
    p.rolls++;
    ev(s, { t: 'senda', kind: 'roll', npc: '', x: e.x, y: e.y, n: e.facing });
    return true;
  }
  if (p.mode === 2) {
    e.anim = 'walk';
    const nx = e.x + idiv(p.dx * R.speed, 1000);
    const ny = e.y + idiv(p.dy * R.speed, 1000);
    // Its front: half a hex ahead of it.
    const front = cellAtWorld(s, nx + idiv(p.dx * 450, 1000), ny + idiv(p.dy * 450, 1000));
    const ci = cairnAt(r, front);
    if (ci >= 0) {
      // Against a standing cairn: it stops dead, stunned; the cairn stands.
      const c = r.cairns[ci] as FallenRules['cairns'][number];
      e.stun = R.stun;
      p.mode = 3;
      p.t = s.tick + R.stun + R.rest;
      p.stuns++;
      ev(s, { t: 'senda', kind: 'rollStun', npc: '', x: c.x, y: c.y, n: ci });
      sendaCue(s, `stun${p.stuns}`);
      return true;
    }
    p.left -= R.speed;
    if (p.left <= 0 || !heroWalkable(s, front)) {
      p.mode = 3;
      p.t = s.tick + R.rest;
      ev(s, { t: 'senda', kind: 'rollEnd', npc: '', x: e.x, y: e.y });
      return true;
    }
    e.x = nx;
    e.y = ny;
    for (const o of s.heroes) {
      if (p.struck || o.downTicks > 0 || !withinRange(o.x, o.y, e.x, e.y, R.r)) continue;
      p.struck = true;
      damageHero(s, o, mulBp(o.stats.maxHp, R.heroBp), e, 'physical', true);
      // Thrown a hex along its way, if there is floor there.
      const px = o.x + idiv(p.dx * R.push, 1000);
      const py = o.y + idiv(p.dy * R.push, 1000);
      if (heroWalkable(s, cellAtWorld(s, px, py))) {
        o.x = o.ax = px;
        o.y = o.ay = py;
        o.path = [];
        o.moving = false;
      }
      ev(s, { t: 'senda', kind: 'rollHit', npc: '', x: o.x, y: o.y });
    }
    return true;
  }
  // Resting after a roll.
  e.anim = 'idle';
  if (s.tick >= p.t) {
    p.mode = 0;
    p.next = s.tick + R.every;
  }
  return true;
};

export const fallenTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.fallen;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.fallen ??= fresh(r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (s.waveT >= w.next.sling) {
    w.next.sling = s.waveT + r.sling.every;
    band(s, r, 'slinger', r.sling.n);
  }
  if (s.waveT >= w.next.crow && h) {
    w.next.crow = s.waveT + r.crows.every;
    band(s, r, 'crow', r.crows.n, h);
  }
  if (s.waveT >= r.roller.at) rollerIn(s, r, w, cue);
  if (!h) return;
  // A cairn read.
  if (w.job >= 0) {
    const c = r.cairns[w.job] as FallenRules['cairns'][number];
    // (A blow doesn't start it over here: on the slope there is always something throwing.)
    if (withinRange(h.x, h.y, c.x, c.y, r.reach) && !h.moving) {
      w.hp = h.hp;
      if (++w.held >= r.read) {
        const i = w.job;
        w.job = -1;
        w.held = 0;
        w.read[i] = 1;
        w.at = s.tick;
        if (w.rings[1] !== i) w.rings = [w.rings[1] as number, i];
        ev(s, { t: 'senda', kind: 'cairnRead', npc: '', x: c.x, y: c.y, n: i });
        if (i === r.long) cue('long');
      }
    } else {
      w.held = 0;
      w.hp = h.hp;
    }
  }
  // On a base: standing `stand` there settles it.
  let on = -1;
  for (let i = 0; i < r.bases.length; i++) {
    const b = r.bases[i] as FallenRules['bases'][number];
    if (w.bases[i] === 0 && withinRange(h.x, h.y, b.x, b.y, 450)) on = i;
  }
  if (on !== w.on) {
    w.on = on;
    w.since = s.tick;
    if (on >= 0) ev(s, { t: 'senda', kind: 'baseOn', npc: '', x: h.x, y: h.y, n: on });
  }
  if (on < 0 || h.moving || s.tick - w.since < r.stand) return;
  const b = r.bases[on] as FallenRules['bases'][number];
  if (on === r.truth) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'baseRight', npc: '', x: b.x, y: b.y, n: on });
    return;
  }
  // Someone else's cairn stood there: the plate cracks, and the noise brings them.
  w.bases[on] = 1;
  w.on = -1;
  w.wrongs++;
  ev(s, { t: 'senda', kind: 'baseWrong', npc: '', x: b.x, y: b.y, n: on });
  cue(`wrong${w.wrongs}`);
  band(s, r, 'slinger', r.wrong.sling);
  band(s, r, 'crow', r.wrong.crows, h);
  if (!w.roller.id) rollerIn(s, r, w, cue);
  else if (w.roller.mode === 0) w.roller.next = s.tick;
};

export const fallenGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.fallen;
  return { done: w && w.done >= 0 ? 1 : 0, need: 1 };
};

export const fallenResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.fallen;
  if (!w || w.done < 0) return null;
  return s.tick >= w.done + 18 ? 'won' : null;
};
